/**
 * ProjectResultsView — the single shared "project results" surface used by BOTH
 * the customer project page (pages/user/ProjectDetail) and the admin project
 * page (pages/admin/ProjectDetail).
 *
 * It owns:
 *   • the Classic | Range dashboard view switcher (Range is the DEFAULT; Classic
 *     is opt-in via ?view=classic, persisted in the URL),
 *   • the RangeModeProvider that both the Range dashboard and the Classic
 *     RangeModeToggle depend on,
 *   • the "no completed run yet" fallback for the Range view.
 *
 * The two pages pass their own content in:
 *   • `rangeContent`  — a fully-configured <RangeDashboard/> element (same
 *      component on both pages → WYSIWYG, never forked),
 *   • `rangeExtras`   — must-port extras shown BELOW the dashboard in Range mode
 *      (e.g. the hourly ImpactExplorer, scenario management), already gated by
 *      the caller as needed,
 *   • `classicContent`— the page's existing Classic layout, unchanged.
 *
 * Only one branch mounts at a time, so passing the same element into both
 * `rangeExtras` and inside `classicContent` never double-renders.
 */

import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { RangeModeProvider } from "../impact/context/RangeModeContext";

export interface ProjectResultsViewProps {
  /** True when a completed run + impact totals exist (Range needs real data). */
  hasResults: boolean;
  /** The configured <RangeDashboard/> element. */
  rangeContent: ReactNode;
  /** Extras rendered under the dashboard in Range mode (hourly explorer, …). */
  rangeExtras?: ReactNode;
  /** The page's existing Classic results layout. */
  classicContent: ReactNode;
  /** Optional custom "no run yet" fallback for Range mode. */
  noRunFallback?: ReactNode;
}

export function ProjectResultsView({
  hasResults,
  rangeContent,
  rangeExtras,
  classicContent,
  noRunFallback,
}: ProjectResultsViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Range dashboard is the default view; Classic is opt-in (?view=classic).
  const dashView = searchParams.get("view") === "classic" ? "classic" : "range";
  const setDashView = (v: "classic" | "range") => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === "classic") next.set("view", "classic");
        else next.delete("view");
        return next;
      },
      { replace: true },
    );
  };

  return (
    <RangeModeProvider>
      {/* Range dashboard | Classic view switcher (Range is the default). */}
      <div className="flex justify-end mb-3">
        <div className="inline-flex items-center bg-surface-700 border border-border rounded-lg p-0.5">
          {(["range", "classic"] as const).map((v) => (
            <button
              key={v}
              onClick={() => setDashView(v)}
              aria-pressed={dashView === v}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                dashView === v
                  ? "bg-surface-500 text-text-primary shadow-sm"
                  : "text-text-muted hover:text-text-secondary"
              }`}
            >
              {v === "range" ? "Modern" : "Classic"}
            </button>
          ))}
        </div>
      </div>

      {dashView === "range" ? (
        hasResults ? (
          <>
            {rangeContent}
            {rangeExtras}
          </>
        ) : (
          noRunFallback ?? (
            <div className="rounded-lg bg-surface-700 p-5 border border-border">
              <p className="text-sm text-text-secondary">
                <span className="font-medium text-text-primary">
                  No completed model run yet.
                </span>{" "}
                Switch to Classic to run the model, then return here for the Range
                dashboard.
              </p>
            </div>
          )
        )
      ) : (
        classicContent
      )}
    </RangeModeProvider>
  );
}

export default ProjectResultsView;
